import * as _ from "lodash";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_SubjectAreaRow} from "../PA.Core/PAdbSchemaRows";
import {IwbSubjectArea} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Metric_Definition,
    esWB_MetricDefinition_GLAccount,
    esWB_Subject_Area
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {PAEntity} from "../PA.Core/PAEntity";
import {SubjectAreaBOValidation} from "./SubjectAreaBO.validation";
import {IPolicyCollection} from "../PA.Core/PA.Core.IF.Policy";
import {IHaveSecurity} from "../PA.Core/PA.Core.IF.Aspects";
import {PolicyCollection} from "../PA.Core/Security/PolicyCollection";
import {PolicyBOis} from "../PA.Core/Security/PolicyBOis";
import {PolicyHasRole} from "../PA.Core/Security/PolicyHasRole";
import {SubjectAreaPolicy} from "./SubjectAreaPolicy";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {PAEnvironment} from "../PA.Core/PAEnvironment";


export class SubjectAreaBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_SubjectArea: WB_SubjectAreaRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Subject Area'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_SubjectArea = null;

        this.IHaveSecurity_init()
    }

    get WB_SubjectArea(): WB_SubjectAreaRow {
        return this.drWB_SubjectArea;
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Subject Area");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Subject Area Unit";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Subject_Area)
                .then((newSubjectAreaRow: WB_SubjectAreaRow) => {
                    newSubjectAreaRow.setDefaults();
                    self.drWB_SubjectArea = newSubjectAreaRow;
                    //add this to all the BOs
                    self.dbContext.add(esWB_Subject_Area, newSubjectAreaRow);
                    self.Identity.setAll(self.WB_SubjectArea.cif.WB_SUBJECTAREA_ID, self.WB_SubjectArea.cif.SUBJECT_AREA_ID, self.WB_SubjectArea.cif.SUBJECT_AREA_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SubjectArea, "WB_SUBJECTAREA_ID", "SUBJECT_AREA_ID", "SUBJECT_AREA_NAME");

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err)
                })

        });
        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {

            if (self.bIsLoaded) {
                let sError = "Subject Area already loaded";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Subject Area";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = SubjectAreaBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = SubjectAreaBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aSubjectAreaRow: any = recordResolver.get(esWB_Subject_Area, anId);

                    if (_.isEmpty(aSubjectAreaRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_SubjectArea = aSubjectAreaRow;
                    self.Identity.setAll(self.WB_SubjectArea.cif.WB_SUBJECTAREA_ID, self.WB_SubjectArea.cif.SUBJECT_AREA_ID, self.WB_SubjectArea.cif.SUBJECT_AREA_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SubjectArea, "WB_SUBJECTAREA_ID", "SUBJECT_AREA_ID", "SUBJECT_AREA_NAME");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                })
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        if (this._bIsLoaded) {
            return ASync.resolve(this)
        }

        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_SubjectArea.cif.SUBJECT_AREA_NAME + "(" + self.WB_SubjectArea.cif.SUBJECT_AREA_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            //Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext to Validate Subject Area";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            SubjectAreaBOValidation.ValidateFields(self)
                .then((ignore) => {
                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                }).catch((err) => {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
                resolve(self);
            });
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        let aPAEntity = new PAEntity();
        const wbSubjectArea = <IwbSubjectArea>self.drWB_SubjectArea;
        this.SaveProgressProvider.start("Saving " + wbSubjectArea.SUBJECT_AREA_NAME + "(" + wbSubjectArea.SUBJECT_AREA_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Subject Area.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let bDone = false;
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }

                    let tasks: Promise<any> [] = [];

                    tasks.push(saveFunction(esWB_Subject_Area, self.drWB_SubjectArea, {}));
                    return Promise.all(tasks);
                }).then((ignored) => {
                if (bDone) return;

                if (bBatch) {
                    self.dbContext.addBatchCompleteListener(() => {
                        self._bIsSaved = true;
                        LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.Lease_Type_SubjectArea);
                        return aPAEntity.syncSubjectArea(self.drWB_SubjectArea.cif.WB_SUBJECTAREA_ID);
                    })
                } else {
                    self._bIsSaved = true;
                    LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.Lease_Type_SubjectArea);
                    return aPAEntity.syncSubjectArea(self.drWB_SubjectArea.cif.WB_SUBJECTAREA_ID);
                }
            })
                .then((ignore) => {
                    let subjectAreaPolicy: SubjectAreaPolicy = self.PolicyCollection.SubjectArea
                    if(subjectAreaPolicy){
                        subjectAreaPolicy.OnLoadComplete
                    }
                })
                .then(() => {
                self.SaveProgressProvider.finish();
                resolve(self);
            }).catch((err) => {
                self.SaveProgressProvider.exception(err);
                resolve(self)
            })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbSubjectArea = <IwbSubjectArea>self.drWB_SubjectArea;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbSubjectArea.SUBJECT_AREA_NAME + "(" + wbSubjectArea.SUBJECT_AREA_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {

            // 1. Validate if we can delete the Subject Area

            // 2. If Valid, mark all the records for deletion.

            if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                // Don't delete..
                this.DeleteProgressProvider.finish();
                resolve(self)
                return;
            }

            self.drWB_SubjectArea.markDeleted()
            _.forEach(self.drWB_SubjectArea.WB_MetricDefinitionRows, (a) => a.markDeleted())


            if (bSaveNow) {
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const subject_area_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {
                [esWB_Metric_Definition]: [esWB_MetricDefinition_GLAccount]
            }
        };

        const aRecResolver = new ESRecordResolver(dbContext, subject_area_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Subject_Area, ids)
                .then((result) => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }


    //<editor-fold desc="IHaveSecurity operations">

    private _PolicyCollection: IPolicyCollection
    private _bIHaveSecurityLoaded: boolean
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection()
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_BeforeReload() {
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded
                .then((ignore) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this

        if (this.bIHaveSecurityLoaded) {
            return ASync.resolve(this)
        }

        if (this._OnIHaveSecurityLoaded) {
            return this._OnIHaveSecurityLoaded
        }

        let aBOis = new PolicyBOis(['Super_User', 'Subject_Area_Editor'], ['Subject_Area_Viewer'], self.WB_SubjectArea.cif.WB_SUBJECTAREA_ID)
        self.PolicyCollection.addPolicy("BOis", aBOis)

        let aHasRolePolicy: PolicyHasRole
        let aSubjectAreaPolicy: SubjectAreaPolicy;

        this._OnIHaveSecurityLoaded = new Promise<IHaveSecurity>((resolve, error) => {
            aHasRolePolicy = new PolicyHasRole(self.WB_SubjectArea.cif.WB_SUBJECTAREA_ID)
            aHasRolePolicy.OnLoadComplete
                .then((ignore) => {
                    if(self.drWB_SubjectArea._rowExists) {
                        self.PolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                        aSubjectAreaPolicy = new SubjectAreaPolicy(self)
                        return aSubjectAreaPolicy.OnLoadComplete
                    }

                })
                .then((ignore) => {
                    self.PolicyCollection.addPolicy("SubjectArea", aSubjectAreaPolicy)
                    this._bIHaveSecurityLoaded = true
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })

        return this._OnIHaveSecurityLoaded
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded
    }


    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( SubjectAreaBO, "SubjectAreaBO" ,"Subject Area")